import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LegalFooter } from "@/components/LegalFooter";
import { ArrowLeft, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

/**
 * EMRKT.CH — Public 14-day withdrawal page (EU consumer rights, in force
 * since June 2026 + Swiss CO). Reachable in ONE CLICK from the order-success
 * page and the account page. No forced chat, no long form.
 */
export default function WithdrawalPage() {
  const { lang } = useLang();
  const [sp] = useSearchParams();
  const [order, setOrder] = useState(sp.get("order") || "");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { document.title = "Withdrawal · EMRKT.CH"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!order.trim() || !email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await shopApi.post("/shop/withdrawal", {
        order_number: order.trim(),
        email: email.trim().toLowerCase(),
        reason: reason.trim() || null,
      });
      setDone(data);
    } catch (err) {
      setDone({ ok: false, message: err?.response?.data?.detail || "Failed. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="withdrawal-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/shop" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-[color:var(--hq-cyan)] flex items-center justify-center">
              <span className="font-mono text-[10px] text-[color:var(--hq-cyan)]">SH</span>
            </div>
            <div className="font-display font-medium text-sm">EMRKT.CH</div>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-10">
        <Link to="/shop" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6"
              data-testid="withdrawal-back">
          <ArrowLeft size={14} /> back
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={18} className="text-[color:var(--hq-cyan)]" />
          <h1 className="font-display text-3xl">{t(lang, "wd_title")}</h1>
        </div>
        <p className="text-white/60 mb-6 leading-relaxed text-sm">
          {t(lang, "wd_intro")}
        </p>

        {done?.ok ? (
          <div className="hq-card p-6 border-l-2 border-[color:var(--hq-success)]" data-testid="wd-success">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={16} className="text-[color:var(--hq-success)]" />
              <div className="font-mono text-[10px] uppercase text-[color:var(--hq-success)]">confirmed</div>
            </div>
            <div className="text-white text-sm mb-2">{done.message}</div>
            {done.auto_eligible && (
              <div className="text-white/70 text-xs">{t(lang, "wd_auto")}</div>
            )}
            <div className="mt-3 font-mono text-[10px] text-white/40">ref: {done.request_id}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="hq-card p-6 space-y-4" data-testid="wd-form">
            <div>
              <label className="font-mono text-[10px] uppercase text-white/40">{t(lang, "wd_order")}</label>
              <input
                data-testid="wd-order-input"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                required
                className="hq-input w-full mt-1"
                placeholder="e.g. 1234"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-white/40">{t(lang, "wd_email")}</label>
              <input
                data-testid="wd-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="hq-input w-full mt-1"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase text-white/40">{t(lang, "wd_reason")}</label>
              <textarea
                data-testid="wd-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="hq-input w-full mt-1 h-20 text-sm"
                placeholder={t(lang, "wd_reason_ph")}
              />
            </div>
            <button
              data-testid="wd-submit-btn"
              type="submit"
              disabled={busy || !order.trim() || !email.trim()}
              className="hq-btn hq-btn-primary w-full disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              {busy ? t(lang, "wd_sending") : t(lang, "wd_submit")}
            </button>
            <p className="font-mono text-[10px] text-white/40 leading-relaxed">
              {t(lang, "wd_footer")}
            </p>
          </form>
        )}
      </div>

      <LegalFooter />
    </div>
  );
}
